import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  useTheme,
  CircularProgress,
  TextField,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Avatar,
  Stack,
  Badge,
  useMediaQuery,
} from "@mui/material";
import {
  Today,
  ViewWeek,
  NavigateBefore,
  NavigateNext,
  Search,
  Schedule,
  Person,
  CalendarMonth,
  Engineering,
  Description,
} from "@mui/icons-material";
import { format } from "date-fns";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { url } from "../../constants/url";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";

const FIRCalendar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userData, token, hasRole } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [view, setView] = useState("timeGridWeek");
  const [projectsData, setProjectsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [calendarApi, setCalendarApi] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [overlapDialog, setOverlapDialog] = useState({
    open: false,
    time: null,
    projects: [],
  });
  const [engineersMap, setEngineersMap] = useState({});

  /** THEME COLORS */
  const CALENDAR_COLORS = {
    primary: COLORS.PRIMARY,
    secondary: COLORS.SECONDARY,
    accent: COLORS.LIGHT_SECONDARY,
    background: theme.palette.mode === "light" ? COLORS.BACKGROUND : "#0a1929",
    cardBackground: theme.palette.mode === "light" 
      ? "rgba(255,255,255,0.98)" 
      : "rgba(10, 25, 41, 0.95)",
    border: theme.palette.mode === "light" ? COLORS.BORDER : "rgba(30, 58, 138, 0.6)",
    textPrimary: theme.palette.mode === "light" ? COLORS.TEXT : "#e3f2fd",
    textSecondary: theme.palette.mode === "light" ? COLORS.MUTED_TEXT : "#bbdefb",
  };

  /** Project Colors */
  const PROJECT_COLORS = [
    COLORS.PRIMARY,
    COLORS.SECONDARY,
    "#421A6E", 
    "#652D90", 
    "#8A2BE2", 
    "#9932CC", 
    "#BA55D3", 
    "#DA70D6", 
    "#D8BFD8", 
    "#DDA0DD", 
  ];

  const getProjectColor = (index) => PROJECT_COLORS[index % PROJECT_COLORS.length];

  const fetchEngineerData = async (engineerId) => {
    try {
      if (engineersMap[engineerId]) {
        return engineersMap[engineerId];
      }
      const response = await axios.get(`${url}/api/users/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { userID: engineerId }
      });
      if (response.data?.users && response.data.users.length > 0) {
        const engineer = response.data.users[0];
        setEngineersMap(prev => ({ ...prev, [engineerId]: engineer }));
        return engineer;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const fetchProjectsForCalendar = async () => {
    try {
      setLoading(true);
      
      let urlEndpoint = "";
      let params = { limit: 1000 };

      // Determine Endpoint
      if (hasRole("CLIENT") && userData?.clientRole?.roleType === "Individual") {
         // for individuals show their created requests and maybe projects
         urlEndpoint = `${url}/api/fir/requests`;
      } else {
         urlEndpoint = `${url}/api/projects/all`; 
         // we might need both projects and fir requests for engineers/admins.
         // Let's use projects/all but we also need unmapped FIR requests? 
         // For admins, get projects/all and fir/requests?
         // Actually, let's use the FIR specific endpoints if we want just FIR. But requirement says:
         // Admin - all projects (FIR + Admin added).
         // Engineer - assigned projects (FIR + Admin added).
         // Both FIR and Admin-added projects are in the projects collection!
      }

      if (hasRole("ENGINEER") && userData?._id) {
        params.engineer = userData._id;
      }

      // Fetch projects
      const response = await axios.get(`${url}/api/projects/all`, {
        headers: { Authorization: `Bearer ${token}` },
        params: params
      });

      // If client, we need to fetch their main portal projects too.
      let clientProjects = [];
      if (hasRole("CLIENT")) {
        // also fetch main projects
        try {
           const clientResp = await axios.get(`${url}/api/client/checklist-requests`, {
              headers: { Authorization: `Bearer ${token}` },
              params: { clientID: userData._id }
           });
           if(clientResp.data?.requests) {
              clientProjects = clientResp.data.requests;
           }
        } catch(e){}
      }

      let projects = response.data?.data || [];
      
      // Merge main portal client projects if CLIENT
      if (hasRole("CLIENT")) {
         projects = [...projects, ...clientProjects];
         // deduplicate by id
         projects = projects.filter((v,i,a)=>a.findIndex(v2=>(v2._id===v._id))===i);
      }

      const processedProjects = await Promise.all(
        projects
          .filter(project => {
            // Need a valid date to display on calendar
            return project.firRequestId?.inspectionDateFrom || project.startDate || project.createdAt || project.visitedDate || project.assignedVisitDate;
          })
          .map(async (project, index) => {
            // Priority: FIR inspectionDateFrom > startDate > assignedVisitDate > createdAt
            let displayDate = null;
            if (project.firRequestId?.inspectionDateFrom) {
               displayDate = new Date(project.firRequestId.inspectionDateFrom);
            } else if (project.startDate) {
               displayDate = new Date(project.startDate);
            } else if (project.assignedVisitDate) {
               displayDate = new Date(project.assignedVisitDate);
            } else if (project.createdAt) {
               displayDate = new Date(project.createdAt);
            }

            let engineerName = "Not Assigned";
            if (project.assignedEngineers && project.assignedEngineers.length > 0) {
               const eng = project.assignedEngineers[0];
               if (typeof eng === 'object' && eng._id) {
                  engineerName = eng.name || "Not Assigned";
               } else {
                  const fetched = await fetchEngineerData(eng);
                  engineerName = fetched?.name || "Not Assigned";
               }
            } else if (project.assignedEngineer) {
               if (typeof project.assignedEngineer === 'object' && project.assignedEngineer._id) {
                  engineerName = project.assignedEngineer.name || "Not Assigned";
               } else {
                  const fetched = await fetchEngineerData(project.assignedEngineer);
                  engineerName = fetched?.name || "Not Assigned";
               }
            }

            let projectName = project.projectName || project.nameOfProject || "N/A";
            let clientName = project.clientName || project.nameOfClient || project.clientID?.name || "N/A";
            let typeText = project.source === "FIR" ? "Client Request (FIR)" : (project.source === "Technical" || !project.source ? "Admin Added" : project.source);

            // Special handling for main portal client projects which lack source
            if (hasRole("CLIENT") && !project.source && project.templateTitle) {
                typeText = "Main Portal Visit";
            }

            const isFIR = project.source === "FIR";

            return {
              ...project,
              displayDate,
              engineerName,
              clientName,
              projectName,
              typeText,
              isFIR,
              index: index,
            };
          })
      );

      const filteredProjects = processedProjects
        .filter(project => project.displayDate)
        .sort((a, b) => new Date(a.displayDate) - new Date(b.displayDate));

      setProjectsData(filteredProjects);
      
    } catch (err) {
      console.error("Calendar fetch error:", err);
      setProjectsData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && userData) {
      fetchProjectsForCalendar();
    }
  }, [token, userData]);

  const handleDatesSet = (dateInfo) => {
    setCurrentDate(dateInfo.start);
  };

  const getGroupedProjectsByTime = useMemo(() => {
    const grouped = {};
    projectsData
      .filter((project) => {
        if (!searchQuery) return true;
        const searchLower = searchQuery.toLowerCase();
        return (
          project.projectName?.toLowerCase()?.includes(searchLower) ||
          project.clientName?.toLowerCase()?.includes(searchLower) ||
          project.typeText?.toLowerCase()?.includes(searchLower) ||
          project.engineerName?.toLowerCase()?.includes(searchLower)
        );
      })
      .forEach((project) => {
        if (!project.displayDate) return;
        const start = new Date(project.displayDate);
        const timeKey = start.toISOString().slice(0, 16); 
        
        if (!grouped[timeKey]) {
          grouped[timeKey] = [];
        }
        grouped[timeKey].push({ project, start });
      });
    return grouped;
  }, [projectsData, searchQuery]);

  const calendarEvents = useMemo(() => {
    const events = [];
    const MAX_VISIBLE_EVENTS = isMobile ? 2 : isTablet ? 3 : 4; 

    Object.entries(getGroupedProjectsByTime).forEach(([timeKey, projectsAtTime]) => {
      const visibleProjects = projectsAtTime.slice(0, MAX_VISIBLE_EVENTS);
      const hiddenProjects = projectsAtTime.slice(MAX_VISIBLE_EVENTS);

      visibleProjects.forEach(({ project, start }) => {
        const end = new Date(start.getTime() + 60 * 60 * 1000);
        
        const eventTitle = `${project.projectName} • ${project.engineerName} • ${project.typeText}`;
        
        const extendedProps = {
          project, 
          isOverflow: false,
          time: format(start, 'h:mm a'),
          typeText: project.typeText,
          projectName: project.projectName,
          clientName: project.clientName,
          engineerName: project.engineerName,
          isFIR: project.isFIR,
          displayDate: project.displayDate,
        };

        events.push({
          id: project._id,
          title: eventTitle,
          start,
          end,
          extendedProps: extendedProps,
          backgroundColor: alpha(getProjectColor(project.index), 0.95),
          borderColor: getProjectColor(project.index),
          textColor: "#ffffff",
          display: "block",
          classNames: ["premium-event"],
        });
      });

      if (hiddenProjects.length > 0) {
        const firstProject = hiddenProjects[0];
        const start = firstProject.start;
        const end = new Date(start.getTime() + 60 * 60 * 1000);
        
        events.push({
          id: `overflow-${timeKey}`,
          title: `+${hiddenProjects.length} more`,
          start,
          end,
          extendedProps: { 
            isOverflow: true,
            hiddenProjects: hiddenProjects.map(item => item.project),
            time: format(start, 'h:mm a')
          },
          backgroundColor: alpha(COLORS.GREY_COLOR, 0.9),
          borderColor: COLORS.MUTED_TEXT,
          textColor: COLORS.TEXT,
          display: "block",
          classNames: ["overflow-event"],
        });
      }
    });

    return events;
  }, [getGroupedProjectsByTime, isMobile, isTablet]);

  const handleEventClick = (clickInfo) => {
    const { isOverflow, hiddenProjects, time, project } = clickInfo.event.extendedProps;
    
    if (isOverflow && hiddenProjects) {
      setOverlapDialog({
        open: true,
        time: time,
        projects: hiddenProjects,
      });
    } else {
      const projectId = clickInfo.event.id;
      if(project && project.templateTitle && !project.source) {
          // main portal project
          navigate(`/projects/${projectId}`);
      } else {
          // fir/technical project
          navigate(`/technical/project-details/${projectId}`);
      }
    }
  };

  const getFormattedDate = () => {
    if (view === 'timeGridDay') {
      return format(currentDate, 'EEEE, MMMM d, yyyy');
    } else {
      return format(currentDate, 'MMMM yyyy');
    }
  };

  const getCalendarTitle = () => {
    if (hasRole("ENGINEER")) return "My Assigned Technical Projects";
    if (hasRole("CLIENT")) return "My Visits (Main & FIR)";
    return "All Technical Projects";
  };

  const renderEventContent = (eventInfo) => {
    const { isOverflow, projectName, engineerName, typeText, isFIR } = eventInfo.event.extendedProps;
    
    if (isOverflow) {
      return (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '0 4px',
        }}>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: isMobile ? '0.6rem' : '0.65rem',
              fontWeight: 600,
              color: COLORS.TEXT,
              textAlign: 'center',
              lineHeight: 1,
            }}
          >
            {eventInfo.event.title}
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        padding: '0 4px',
        overflow: 'hidden',
        gap: '4px',
        position: 'relative',
      }}>
        <Box sx={{
          position: 'absolute',
          top: 1,
          right: 2,
          fontSize: '0.5rem',
          fontWeight: 600,
          color: 'rgba(255,255,255,0.9)',
          background: isFIR ? COLORS.WARNING : COLORS.INFO,
          borderRadius: '2px',
          padding: '0 2px',
          zIndex: 10,
          minWidth: '14px',
          textAlign: 'center',
          lineHeight: 1.2,
        }}>
          {isFIR ? 'FIR' : 'ADM'}
        </Box>

        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: isMobile ? '0.6rem' : '0.65rem',
            fontWeight: 600,
            color: '#ffffff',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexShrink: 1,
            minWidth: '40px',
            lineHeight: 1.2,
          }}
        >
          {projectName}
        </Typography>

        <Box sx={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.7)', flexShrink: 0, lineHeight: 1 }}>•</Box>

        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: isMobile ? '0.55rem' : '0.6rem',
            fontWeight: 500,
            color: 'rgba(255,255,255,0.9)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexShrink: 1,
            minWidth: '35px',
            lineHeight: 1.2,
          }}
        >
          {engineerName}
        </Typography>

        <Box sx={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.7)', flexShrink: 0, lineHeight: 1 }}>•</Box>

        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: isMobile ? '0.55rem' : '0.6rem',
            fontWeight: 400,
            color: 'rgba(255,255,255,0.8)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexShrink: 1,
            minWidth: '35px',
            lineHeight: 1.2,
          }}
        >
          {typeText}
        </Typography>
      </Box>
    );
  };

  const headerUI = (
    <Paper
      elevation={0}
      sx={{
        px: { xs: 1, sm: 2, md: 3, lg: 4 },
        py: { xs: 1.5, sm: 2, md: 3 },
        borderRadius: 0,
        backdropFilter: "blur(20px)",
        background: CALENDAR_COLORS.cardBackground,
        borderBottom: `1px solid ${CALENDAR_COLORS.border}`,
        display: "flex",
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: "space-between",
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 0 },
        position: "sticky",
        top: 0,
        zIndex: 20,
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2, md: 3 }, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"}
            sx={{ 
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              color: CALENDAR_COLORS.textPrimary,
              fontSize: isMobile ? TYPOGRAPHY.H4.fontSize : TYPOGRAPHY.H2.fontSize,
              lineHeight: isMobile ? TYPOGRAPHY.H4.lineHeight : TYPOGRAPHY.H2.lineHeight,
            }}
          >
            {getCalendarTitle()}
          </Typography>
        </Box>

        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 0.5,
          background: alpha(CALENDAR_COLORS.secondary, 0.1),
          borderRadius: '8px',
          px: { xs: 1, sm: 1.5 },
          py: { xs: 0.5, sm: 0.75 },
          flexShrink: 0,
        }}>
          <CalendarMonth sx={{ color: CALENDAR_COLORS.secondary, fontSize: isMobile ? 16 : 20 }} />
          <Typography 
            variant={isMobile ? "body2" : "subtitle1"}
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 600,
              color: CALENDAR_COLORS.textPrimary,
              fontSize: isMobile ? TYPOGRAPHY.BODY_STANDARD.fontSize : TYPOGRAPHY.BODY_LARGE.fontSize,
            }}
          >
            {getFormattedDate()}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ 
        display: "flex", 
        alignItems: "center", 
        gap: { xs: 1, sm: 2, md: 3 },
        flexWrap: 'wrap',
        justifyContent: { xs: 'space-between', sm: 'flex-end' },
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, order: { xs: 2, sm: 1 } }}>
          <IconButton onClick={() => calendarApi?.prev()} size={isMobile ? "small" : "medium"} sx={{ color: CALENDAR_COLORS.textSecondary, background: alpha(CALENDAR_COLORS.primary, 0.08), "&:hover": { background: alpha(CALENDAR_COLORS.primary, 0.15), transform: "translateY(-2px)" } }}><NavigateBefore /></IconButton>
          <IconButton onClick={() => calendarApi?.today()} size={isMobile ? "small" : "medium"} sx={{ color: CALENDAR_COLORS.textSecondary, background: alpha(CALENDAR_COLORS.primary, 0.08), "&:hover": { background: alpha(CALENDAR_COLORS.primary, 0.15), transform: "translateY(-2px)" } }}><Today /></IconButton>
          <IconButton onClick={() => calendarApi?.next()} size={isMobile ? "small" : "medium"} sx={{ color: CALENDAR_COLORS.textSecondary, background: alpha(CALENDAR_COLORS.primary, 0.08), "&:hover": { background: alpha(CALENDAR_COLORS.primary, 0.15), transform: "translateY(-2px)" } }}><NavigateNext /></IconButton>
        </Box>

        <TextField
          size="small"
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: CALENDAR_COLORS.textSecondary, fontSize: isMobile ? 18 : 20 }} />,
            sx: { borderRadius: '8px', background: alpha(CALENDAR_COLORS.cardBackground, 0.8), border: `1px solid ${CALENDAR_COLORS.border}`, fontSize: isMobile ? TYPOGRAPHY.BODY_SMALL.fontSize : TYPOGRAPHY.BODY_STANDARD.fontSize }
          }}
          sx={{ width: { xs: '100%', sm: 200, md: 250, lg: 280 }, order: { xs: 1, sm: 2 }, minWidth: { xs: 'auto', sm: 200 } }}
        />

        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(e, v) => v && (setView(v), calendarApi?.changeView(v))}
          size={isMobile ? "small" : "medium"}
          sx={{
            background: alpha(CALENDAR_COLORS.cardBackground, 0.8), borderRadius: '8px', p: 0.25, order: { xs: 3, sm: 3 },
            '& .MuiToggleButton-root': { color: CALENDAR_COLORS.textSecondary, border: 'none', borderRadius: '6px', px: { xs: 2, sm: 2.5 }, py: { xs: 0.5, sm: 0.75 }, fontWeight: 600, textTransform: 'none', fontSize: isMobile ? TYPOGRAPHY.BODY_SMALL.fontSize : TYPOGRAPHY.BODY_STANDARD.fontSize, '&.Mui-selected': { background: CALENDAR_COLORS.primary, color: 'white' }, '&:hover': { background: alpha(CALENDAR_COLORS.primary, 0.1), color: CALENDAR_COLORS.textPrimary }, minWidth: { xs: '60px', sm: '80px' } }
          }}
        >
          <ToggleButton value="timeGridWeek"><ViewWeek sx={{ mr: 0.5, fontSize: isMobile ? 16 : 18 }} />{!isMobile && "Week"}</ToggleButton>
          <ToggleButton value="timeGridDay"><Today sx={{ mr: 0.5, fontSize: isMobile ? 16 : 18 }} />{!isMobile && "Day"}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Paper>
  );

  if (loading) return <Box sx={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", background: CALENDAR_COLORS.background }}><CircularProgress size={50} sx={{ color: CALENDAR_COLORS.primary }} /></Box>;

  const calendarCSS = `
    .fc { font-family: ${FONT_FAMILY}; background: ${CALENDAR_COLORS.background}; }
    .fc-toolbar { display: none !important; }
    .fc .fc-col-header-cell { background: ${CALENDAR_COLORS.cardBackground}; border: 1px solid ${CALENDAR_COLORS.border}; padding: ${isMobile ? '8px 4px' : '12px 8px'}; }
    .fc .fc-col-header-cell-cushion { color: ${CALENDAR_COLORS.textPrimary}; font-weight: 700; font-size: ${isMobile ? '0.75rem' : '0.85rem'}; text-transform: uppercase; }
    .fc .fc-timegrid-slot { height: ${isMobile ? '45px' : '50px'} !important; border-bottom: 1px solid ${alpha(CALENDAR_COLORS.border, 0.3)}; background: ${CALENDAR_COLORS.cardBackground}; }
    .fc .fc-timegrid-slot-label { color: ${CALENDAR_COLORS.textSecondary}; font-weight: 600; font-size: ${isMobile ? '0.7rem' : '0.75rem'}; padding: ${isMobile ? '4px 6px' : '8px 12px'}; border: none !important; }
    .fc .fc-timegrid-slot.fc-timegrid-slot-minor { border: none !important; display: none !important; }
    .fc-event { border-radius: 4px !important; border: none !important; padding: 0 !important; box-shadow: 0 1px 4px rgba(0,0,0,0.1) !important; margin: 1px 2px !important; border-left: 2px solid transparent !important; min-height: ${isMobile ? '28px' : '32px'} !important; max-height: ${isMobile ? '30px' : '34px'} !important; overflow: hidden !important; cursor: pointer !important; }
    .fc-event:hover { transform: translateY(-1px) !important; box-shadow: 0 3px 10px rgba(0,0,0,0.15) !important; z-index: 1000 !important; }
    .fc-event-main { padding: 0 !important; height: 100% !important; }
    .fc-event-main-frame { display: flex !important; flex-direction: column !important; height: 100% !important; justify-content: center !important; }
    .fc-event-time, .fc-event-title { display: none !important; }
    .fc-event-title-container { flex: 1 !important; display: flex !important; flex-direction: column !important; justify-content: center !important; }
    .overflow-event { background: linear-gradient(135deg, ${alpha(COLORS.GREY_COLOR, 0.95)}, ${alpha(COLORS.MUTED_TEXT, 0.9)}) !important; border-left-color: ${COLORS.MUTED_TEXT} !important; font-weight: 600 !important; cursor: pointer !important; min-height: ${isMobile ? '24px' : '28px'} !important; max-height: ${isMobile ? '26px' : '30px'} !important; display: flex !important; align-items: center !important; justify-content: center !important; }
    .fc-timegrid-event-harness { position: absolute !important; margin-bottom: 1px !important; z-index: 1 !important; }
    .fc-timegrid-event-harness + .fc-timegrid-event-harness { margin-top: ${isMobile ? '30px' : '34px'} !important; }
    .fc-timegrid-event-harness > .fc-timegrid-event { position: relative !important; width: 96% !important; left: 2% !important; }
    .fc-timegrid-event-harness:hover { z-index: 1000 !important; }
    .fc-day-today { background: linear-gradient(135deg, ${alpha(CALENDAR_COLORS.primary, 0.08)}, ${alpha(CALENDAR_COLORS.secondary, 0.04)}) !important; }
    .fc-day-today .fc-daygrid-day-number { background: ${CALENDAR_COLORS.primary}; color: white !important; border-radius: 8px; width: ${isMobile ? '24px' : '28px'}; height: ${isMobile ? '24px' : '28px'}; display: flex; align-items: center; justify-content: center; font-weight: 800; }
    .fc .fc-timegrid-axis { background: ${CALENDAR_COLORS.cardBackground}; border-right: 1px solid ${CALENDAR_COLORS.border}; color: ${CALENDAR_COLORS.textSecondary}; font-weight: 600; padding: ${isMobile ? '4px 6px' : '8px 12px'}; }
    .fc .fc-timegrid-col { border-right: 1px solid ${CALENDAR_COLORS.border}; background: ${CALENDAR_COLORS.cardBackground}; }
    .fc-scroller::-webkit-scrollbar { width: 6px; }
    .fc-scroller::-webkit-scrollbar-track { background: ${alpha(CALENDAR_COLORS.border, 0.2)}; }
    .fc-scroller::-webkit-scrollbar-thumb { background: ${alpha(CALENDAR_COLORS.primary, 0.4)}; border-radius: 3px; }
    .fc-timeGridDay-view .fc-timegrid-slot { height: ${isMobile ? '40px' : '45px'} !important; }
    .fc-timeGridDay-view .fc-event { min-height: ${isMobile ? '25px' : '28px'} !important; max-height: ${isMobile ? '27px' : '30px'} !important; }
  `;

  return (
    <Box sx={{ background: CALENDAR_COLORS.background, minHeight: "100vh", overflow: 'hidden' }}>
      <style>{calendarCSS}</style>
      {headerUI}
      <Box sx={{ height: { xs: "calc(100vh - 120px)", sm: "calc(100vh - 100px)", md: "calc(100vh - 88px)" }, overflow: 'hidden' }}>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={view}
          events={calendarEvents}
          eventClick={handleEventClick}
          headerToolbar={false}
          ref={(ref) => ref && setCalendarApi(ref.getApi())}
          slotMinTime="07:00:00"
          slotMaxTime="22:00:00"
          allDaySlot={false}
          eventOverlap={false}
          nowIndicator={false}
          dayMaxEvents={true}
          eventMaxStack={isMobile ? 4 : isTablet ? 5 : 6}
          height="100%"
          eventDisplay="block"
          eventOrder="start"
          slotDuration="01:00:00"
          slotLabelFormat={{ hour: 'numeric', hour12: true, meridiem: 'short' }}
          slotLabelInterval="01:00:00"
          datesSet={handleDatesSet}
          eventContent={renderEventContent}
          eventDidMount={(info) => {
            if (info.event.extendedProps.isOverflow) {
              info.el.style.borderLeftColor = COLORS.MUTED_TEXT;
              info.el.style.minHeight = isMobile ? "24px" : "28px";
              info.el.style.maxHeight = isMobile ? "26px" : "30px";
            } else {
              info.el.style.borderLeftColor = info.event.borderColor;
            }
            info.el.style.cursor = 'pointer';
            if (!info.event.extendedProps.isOverflow) {
              const { projectName, clientName, typeText, engineerName, time, isFIR } = info.event.extendedProps;
              info.el.title = `Project: ${projectName}\nClient: ${clientName}\nType: ${typeText}\nEngineer: ${engineerName}\nTime: ${time}`;
            }
          }}
        />
      </Box>

      <Dialog
        open={overlapDialog.open}
        onClose={() => setOverlapDialog({ open: false, time: null, projects: [] })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, background: CALENDAR_COLORS.cardBackground, border: `1px solid ${CALENDAR_COLORS.border}`, maxHeight: '90vh', height: 'auto', minHeight: '70vh' } }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${CALENDAR_COLORS.border}`, pb: 2 }}>
          <Typography variant="h6" fontWeight={700} color={CALENDAR_COLORS.textPrimary} gutterBottom>
            Projects at {overlapDialog.time}
          </Typography>
          <Typography variant="body2" color={CALENDAR_COLORS.textSecondary}>
            {overlapDialog.projects.length} additional projects scheduled at this time
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: { xs: 2, sm: 3 }, maxHeight: 'calc(90vh - 120px)', overflow: 'auto' }}>
          <List sx={{ pt: 0, '& .MuiListItem-root': { marginBottom: 1.5, borderRadius: 2, padding: { xs: 1.5, sm: 2 }, background: alpha(CALENDAR_COLORS.cardBackground, 0.8), border: `1px solid ${CALENDAR_COLORS.border}`, transition: 'all 0.2s ease', '&:hover': { background: alpha(CALENDAR_COLORS.primary, 0.1), borderColor: CALENDAR_COLORS.primary } } }}>
            {overlapDialog.projects.map((project, index) => {
              const projectTime = project.displayDate ? format(new Date(project.displayDate), 'h:mm a') : "N/A";
              return (
                <ListItem key={project._id} sx={{ cursor: 'pointer', padding: '16px' }} onClick={() => navigate(`/technical/project-details/${project._id}`)}>
                  <Badge color="primary" overlap="circular" badgeContent={index + 1} sx={{ '& .MuiBadge-badge': { background: `linear-gradient(135deg, ${getProjectColor(project.index)}, ${alpha(getProjectColor(project.index), 0.7)})`, color: 'white', fontWeight: 700, fontSize: '0.6rem', minWidth: 22, height: 22 } }}>
                    <Avatar sx={{ background: `linear-gradient(135deg, ${getProjectColor(project.index)}, ${alpha(getProjectColor(project.index), 0.7)})`, mr: 2, width: 48, height: 48, fontSize: '1rem', fontWeight: 600 }}>
                      {project.projectName?.charAt(0)?.toUpperCase() || 'P'}
                    </Avatar>
                  </Badge>
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600} color={CALENDAR_COLORS.textPrimary}>{project.projectName}</Typography>
                        <Chip size="small" label={project.isFIR ? 'FIR' : 'ADM'} sx={{ backgroundColor: project.isFIR ? alpha(COLORS.WARNING, 0.1) : alpha(COLORS.INFO, 0.1), color: project.isFIR ? COLORS.WARNING : COLORS.INFO, fontSize: '0.7rem', height: 20, fontWeight: 600 }} />
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={1.5}>
                        <Stack spacing={0.5}>
                          <Stack direction="row" alignItems="center" spacing={1}><Person sx={{ fontSize: 16, color: CALENDAR_COLORS.textSecondary, width: 20 }} /><Typography variant="body2" color={CALENDAR_COLORS.textSecondary}><strong>Client:</strong> {project.clientName}</Typography></Stack>
                          <Stack direction="row" alignItems="center" spacing={1}><Engineering sx={{ fontSize: 16, color: CALENDAR_COLORS.textSecondary, width: 20 }} /><Typography variant="body2" color={CALENDAR_COLORS.textSecondary}><strong>Engineer:</strong> {project.engineerName}</Typography></Stack>
                          <Stack direction="row" alignItems="center" spacing={1}><Description sx={{ fontSize: 16, color: CALENDAR_COLORS.textSecondary, width: 20 }} /><Typography variant="body2" color={CALENDAR_COLORS.textSecondary}><strong>Type:</strong> {project.typeText}</Typography></Stack>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}><Schedule sx={{ fontSize: 16, color: CALENDAR_COLORS.textSecondary, width: 20 }} /><Typography variant="body2" fontWeight={600} color={CALENDAR_COLORS.primary}>{projectTime}</Typography></Stack>
                      </Stack>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FIRCalendar;
